import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2, ArrowLeft, UserPlus } from "lucide-react";

const keyOf = (c) => ["name", "email", "phone", "company"].map((k) => (c[k] || "").trim().toLowerCase()).join("|");

function Row({ c, i, checked, dup, onToggle }) {
  return (
    <button data-testid={`import-review-row-${i}`} onClick={() => onToggle(i)} aria-pressed={checked}
      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg transition-colors ${checked ? "bg-blue-600/10" : "hover:bg-slate-800/60"}`}>
      <span className={`w-5 h-5 shrink-0 rounded-md flex items-center justify-center border-2 ${checked ? "bg-blue-600 border-blue-600 text-white" : "border-slate-500"}`}>
        {checked && <Check className="w-3.5 h-3.5" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-white truncate">{c.name || c.company || c.email || c.phone}</span>
        <span className="block text-xs text-slate-400 truncate">{[c.company, c.phone, c.email].filter(Boolean).join(" · ")}</span>
      </span>
      {dup && <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300 shrink-0">Already saved</span>}
    </button>
  );
}

export default function ImportReview({ contacts, existing, busy, preselect, onBack, onConfirm, onAddMore, addingMore }) {
  const [sel, setSel] = useState(() => new Set(preselect ? contacts.map((_, i) => i) : []));
  useEffect(() => {
    if (preselect) setSel((s) => new Set([...s, ...contacts.map((_, i) => i)]));
  }, [contacts, preselect]);
  const [q, setQ] = useState("");
  const dupKeys = useMemo(() => new Set(existing.map(keyOf)), [existing]);
  const shown = contacts.map((c, i) => [c, i]).filter(([c]) => !q || Object.values(c).join(" ").toLowerCase().includes(q.toLowerCase()));
  const toggle = (i) => setSel((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const setShown = (on) => setSel((s) => { const n = new Set(s); shown.forEach(([, i]) => (on ? n.add(i) : n.delete(i))); return n; });

  return (
    <div className="p-6 pb-0 flex flex-col max-h-[85dvh]" data-testid="import-review">
      <button onClick={onBack} data-testid="import-review-back" className="flex items-center gap-1 text-xs text-slate-400 hover:text-white mb-2 self-start"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
      <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">Choose who to add</h2>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-slate-400" data-testid="import-review-count">{contacts.length} contact{contacts.length === 1 ? "" : "s"} found · {sel.size} selected</p>
        <button data-testid="import-review-confirm-top" disabled={!sel.size || busy} onClick={() => onConfirm(contacts.filter((_, i) => sel.has(i)))}
          className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-semibold transition-colors">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Add {sel.size}
        </button>
      </div>
      <input data-testid="import-review-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts"
        className="w-full mb-2 px-3 py-2 rounded-lg bg-[#1E293B] border border-slate-700 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500" />
      <div className="flex gap-3 text-xs mb-2">
        <button data-testid="import-review-select-all" onClick={() => setShown(true)} className="text-blue-400 hover:text-blue-300">Select all{q ? " shown" : ""}</button>
        <button data-testid="import-review-clear" onClick={() => setShown(false)} className="text-slate-400 hover:text-white">Clear</button>
      </div>
      <div className="flex-1 overflow-y-auto -mx-2 px-2 space-y-0.5 min-h-[120px]">
        {shown.map(([c, i]) => <Row key={i} c={c} i={i} checked={sel.has(i)} dup={dupKeys.has(keyOf(c))} onToggle={toggle} />)}
      </div>
      {onAddMore && (
        <button data-testid="import-review-add-more" onClick={onAddMore} disabled={addingMore || busy}
          className="mt-2 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-dashed border-slate-600 text-slate-300 hover:text-white hover:border-blue-500 text-sm disabled:opacity-50 transition-colors">
          {addingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Pick another person
        </button>
      )}
      <div className="sticky bottom-0 -mx-6 px-6 pt-3 pb-[max(1.5rem,env(safe-area-inset-bottom))] bg-[#111827] border-t border-slate-800 mt-3">
        {!sel.size && <p className="text-xs text-amber-300 text-center mb-2" data-testid="import-review-hint">Tick the people you want, then tap Add.</p>}
        <button data-testid="import-review-confirm" disabled={!sel.size || busy} onClick={() => onConfirm(contacts.filter((_, i) => sel.has(i)))}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-semibold transition-colors">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Add {sel.size} contact{sel.size === 1 ? "" : "s"} to Dextyou
        </button>
      </div>
    </div>
  );
}
